import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {hasPermission, useWardrobe} from '../context/WardrobeContext';
import Spinner from '../components/Spinner';
import AuthImage from '../components/AuthImage';
import {
    AlertTriangleIcon,
    CoinsIcon,
    EllipsisVerticalIcon,
    HeartIcon,
    RecycleIcon,
    ShirtIcon,
    TrashIcon,
    UsersIcon,
} from '../components/icons';
import {
    DISPOSAL_METHOD_LABELS,
    DisposalIntent,
    INTENT_OPTIONS,
    pendingDisposalLabel,
} from '../support/itemLifecycle';
import {DISPOSAL_METHOD_HINTS} from '../support/disposalMethods';
import {pluralItems, pluralDays} from '../utils/plural';
import {DeclutterPendingItem, confirmDisposal, fetchPending, returnToWardrobe} from '../service/DeclutterApi';
import {ItemDeletionImpact, deleteItemRecord, fetchDeletionImpact} from '../service/DisposalApi';
import ConfirmDisposalDialog from '../components/disposal/ConfirmDisposalDialog';
import DeleteRecordDialog from '../components/disposal/DeleteRecordDialog';
import logger from '../utils/logger';

/**
 * Стадия «Ждут передачи» раздела «Выбытие».
 *
 * <p>Решение уже принято, но физически вещь всё ещё у владельца. Экран задаёт ровно один
 * вопрос: что с ней произошло на самом деле. Поэтому здесь нет «очистки»: происходят разные
 * события, и у каждого свой способ и своя дата. Ошибочно заведённая запись удаляется отдельно
 * и выбытием не считается.</p>
 *
 * <p>На карточку приходится <b>одна</b> основная кнопка — запланированный способ (D11). Раньше
 * их было до шести, включая разрушительное удаление записи, и экран превращался в список
 * кнопок. Смена способа стала осознанным вторым шагом с определениями, а возврат и удаление
 * уехали в меню дополнительных действий.</p>
 */

/** Намерение шире редактируемого набора: продажу выставляет подтверждение по объявлению. */
type PendingIntent = DisposalIntent | 'SOLD' | null | undefined;

interface ClothingItem {
    id: string;
    name: string;
    categoryName: string | null;
    brandName: string | null;
    sizeName: string | null;
    conditionName: string | null;
    colorNames: string | null;
    purchaseDate: string | null;
    disposalDate: string | null;
    firstPhotoUrl: string | null;
    disposalIntent?: PendingIntent;
}

interface PageResponse {
    content: ClothingItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

const PAGE_SIZE = 50;

/** Порядок групп: сначала то, что даёт вещи вторую жизнь, свалка — последней. */
const GROUP_ORDER: PendingIntent[] = ['GIVEN_AWAY', 'DONATED', 'RECYCLED', 'DISPOSED', 'SOLD', null];

const GROUP_TITLES: Record<string, string> = {
    GIVEN_AWAY: 'Отдать человеку',
    DONATED: 'Пожертвовать',
    RECYCLED: 'Сдать на переработку',
    DISPOSED: 'Выбросить',
    SOLD: 'Продаётся',
    NONE: 'Без решения',
};

const GROUP_HINTS: Record<string, string> = {
    SOLD: 'Продажу закрывает объявление с фактической суммой — здесь её подтвердить нельзя.',
    NONE: 'Способ не выбран. Пока его нет, вещь считается выброшенной — выберите, что с ней произошло.',
};

const groupKey = (intent: PendingIntent): string => intent ?? 'NONE';

/** Иконка направления: отдать людям, пожертвовать, переработать, продать, выбросить. */
const IntentIcon: React.FC<{intent: PendingIntent; className?: string}> = ({intent, className = 'w-4 h-4'}) => {
    if (intent === 'DONATED') return <HeartIcon className={className}/>;
    if (intent === 'GIVEN_AWAY') return <UsersIcon className={className}/>;
    if (intent === 'SOLD') return <CoinsIcon className={className}/>;
    if (intent === 'RECYCLED') return <RecycleIcon className={className}/>;
    return <TrashIcon className={className}/>;
};

/** Судьба вещи без оговорки «при очистке»: довести решение до конца можно и по одной вещи. */
const fateLabel = (intent: PendingIntent): string => {
    const text = intent === 'SOLD'
        ? 'будет продано'
        : pendingDisposalLabel((intent ?? 'DISPOSED') as DisposalIntent) ?? 'будет выброшено';
    return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Сегодняшнее число в формате input[type=date] — и значение по умолчанию, и потолок. */
const today = (): string => new Date().toISOString().slice(0, 10);

interface PendingRow extends ClothingItem {
    pending?: DeclutterPendingItem;
}

const TrashBinPage: React.FC = () => {
    const navigate = useNavigate();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();

    const canView = isOwnWardrobe || hasPermission(permissions, 'MOVE_TO_TRASH');
    const canFinalize = isOwnWardrobe || hasPermission(permissions, 'CLEAR_TRASH');
    const canDeleteRecord = isOwnWardrobe;
    const permissionsLoaded = isOwnWardrobe || permissions !== null;

    const [items, setItems] = useState<ClothingItem[]>([]);
    const [pending, setPending] = useState<Map<string, DeclutterPendingItem>>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [date, setDate] = useState(today);
    const [confirming, setConfirming] = useState<{ids: string[]; method: DisposalIntent; names: string[]} | null>(null);
    const [deleting, setDeleting] = useState<{item: ClothingItem; impact: ItemDeletionImpact | null} | null>(null);

    const load = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const api = await createAxiosInstance();
            const params: Record<string, unknown> = {page: 0, size: PAGE_SIZE, sortBy: 'createdAt', sortDir: 'desc'};
            if (selectedOwnerId) params.ownerId = selectedOwnerId;
            const [page, step] = await Promise.all([
                api.get<PageResponse>('/clothing/trash', {params}),
                fetchPending(selectedOwnerId).catch(() => null),
            ]);
            setItems(page.data.content);
            setPending(new Map((step?.items ?? []).map(i => [i.itemId, i])));
            setError(null);
        } catch (e) {
            logger.debug('TrashBinPage: load failed', {error: (e as Error)?.message});
            setItems([]);
            setError('Не удалось загрузить вещи, ожидающие передачи');
        } finally {
            setLoading(false);
            setSelected(new Set());
        }
    }, [canView, selectedOwnerId]);

    useEffect(() => {
        void load();
    }, [load]);

    const rows: PendingRow[] = useMemo(
        () => items.map(i => ({...i, pending: pending.get(i.id)})),
        [items, pending],
    );

    const groups = useMemo(() => {
        const map = new Map<string, PendingRow[]>();
        rows.forEach(row => {
            const key = groupKey(row.disposalIntent);
            const bucket = map.get(key);
            if (bucket) {
                bucket.push(row);
            } else {
                map.set(key, [row]);
            }
        });
        return GROUP_ORDER
            .map(intent => ({intent, key: groupKey(intent), rows: map.get(groupKey(intent)) ?? []}))
            .filter(g => g.rows.length > 0);
    }, [rows]);

    const toggle = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const askConfirm = (ids: string[], method: DisposalIntent) => {
        if (ids.length === 0) return;
        const names = rows.filter(r => ids.includes(r.id)).map(r => r.name);
        setConfirming({ids, method, names});
    };

    const runConfirm = async () => {
        if (!confirming) return;
        const {ids, method} = confirming;
        setConfirming(null);
        setBusy(true);
        try {
            await confirmDisposal(selectedOwnerId, ids, method, date);
            await load();
        } catch (e) {
            logger.debug('TrashBinPage: confirm failed', {error: (e as Error)?.message});
            setError('Не удалось отметить выбытие. Попробуйте ещё раз.');
        } finally {
            setBusy(false);
        }
    };

    const restore = async (ids: string[]) => {
        setBusy(true);
        try {
            await returnToWardrobe(selectedOwnerId, ids);
            await load();
        } catch (e) {
            logger.debug('TrashBinPage: return failed', {error: (e as Error)?.message});
            setError('Не удалось вернуть вещи в гардероб');
        } finally {
            setBusy(false);
        }
    };

    const askDelete = async (item: ClothingItem) => {
        setDeleting({item, impact: null});
        const impact = await fetchDeletionImpact(item.id).catch(() => null);
        // Состав — предупреждение, а не блокировка: без него диалог честно скажет, что не знает.
        setDeleting(current => (current && current.item.id === item.id ? {...current, impact} : current));
    };

    const runDelete = async () => {
        if (!deleting) return;
        const id = deleting.item.id;
        setDeleting(null);
        setBusy(true);
        try {
            await deleteItemRecord(id);
            await load();
        } catch (e) {
            logger.debug('TrashBinPage: delete failed', {error: (e as Error)?.message});
            setError('Не удалось удалить запись');
        } finally {
            setBusy(false);
        }
    };

    if (!permissionsLoaded || loading) {
        return <div className="flex justify-center py-12" data-testid="pending-loading"><Spinner/></div>;
    }

    if (!canView) {
        return (
            <div className="card p-12 text-center" data-testid="pending-no-access">
                <TrashIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"/>
                <p className="text-gray-600 dark:text-gray-400 text-lg">Нет доступа к этой стадии</p>
                <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                    Нужно право принимать решения о выбытии в этом гардеробе
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {confirming && (
                <ConfirmDisposalDialog
                    method={confirming.method}
                    names={confirming.names}
                    date={date}
                    onConfirm={() => void runConfirm()}
                    onCancel={() => setConfirming(null)}
                />
            )}
            {deleting && (
                <DeleteRecordDialog
                    name={deleting.item.name}
                    impact={deleting.impact}
                    onConfirm={() => void runDelete()}
                    onCancel={() => setDeleting(null)}
                />
            )}

            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Ждут передачи{items.length > 0 ? `: ${pluralItems(items.length)}` : ''}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="pending-subtitle">
                        Решение принято, вещи ещё у вас. Отметьте, что с ними произошло на самом
                        деле — приложение запомнит способ и дату.
                    </p>
                </div>
                {items.length > 0 && canFinalize && (
                    <label className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="block mb-1">Дата события</span>
                        <input
                            type="date"
                            value={date}
                            max={today()}
                            onChange={e => setDate(e.target.value)}
                            data-testid="pending-date"
                            className="input-field min-h-[44px]"
                        />
                    </label>
                )}
            </div>

            {error && (
                <p className="text-sm text-rose-600 dark:text-rose-400" role="alert" data-testid="pending-error">
                    {error}
                </p>
            )}

            {items.length === 0 ? (
                <div className="card p-12 text-center" data-testid="pending-empty">
                    <TrashIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4"/>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Ничего не ждёт передачи</p>
                    <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                        Решения по вещам принимаются на стадии «Решения», после них вещи появятся здесь.
                    </p>
                </div>
            ) : (
                groups.map(group => (
                    <PendingGroup
                        key={group.key}
                        intent={group.intent}
                        rows={group.rows}
                        selected={selected}
                        busy={busy}
                        canFinalize={canFinalize}
                        canDeleteRecord={canDeleteRecord}
                        onToggle={toggle}
                        onConfirm={askConfirm}
                        onReturn={ids => void restore(ids)}
                        onDelete={item => void askDelete(item)}
                        onOpen={id => navigate(`/clothing/${id}`)}
                    />
                ))
            )}
        </div>
    );
};

// ── Группа намерения ───────────────────────────────────────────────────────────

interface GroupProps {
    intent: PendingIntent;
    rows: PendingRow[];
    selected: Set<string>;
    busy: boolean;
    canFinalize: boolean;
    canDeleteRecord: boolean;
    onToggle: (id: string) => void;
    onConfirm: (ids: string[], method: DisposalIntent) => void;
    onReturn: (ids: string[]) => void;
    onDelete: (item: ClothingItem) => void;
    onOpen: (id: string) => void;
}

const PendingGroup: React.FC<GroupProps> = ({
    intent, rows, selected, busy, canFinalize, canDeleteRecord,
    onToggle, onConfirm, onReturn, onDelete, onOpen,
}) => {
    const key = groupKey(intent);
    const method: DisposalIntent = intent && intent !== 'SOLD' ? intent : 'DISPOSED';
    const sellOnly = intent === 'SOLD';
    const chosen = rows.filter(r => selected.has(r.id)).map(r => r.id);
    const hint = GROUP_HINTS[key];

    return (
        <section className="card p-4" data-testid={`pending-group-${key}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <IntentIcon intent={intent}/>
                    {GROUP_TITLES[key] ?? key} · {rows.length}
                </h3>
                {canFinalize && !sellOnly && (
                    <button
                        type="button"
                        disabled={busy || chosen.length === 0}
                        onClick={() => onConfirm(chosen, method)}
                        data-testid={`pending-bulk-${key}`}
                        className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm disabled:opacity-50"
                    >
                        {DISPOSAL_METHOD_LABELS[method]} — выбранные ({chosen.length})
                    </button>
                )}
            </div>
            {hint && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid={`pending-hint-${key}`}>
                    {hint}
                </p>
            )}

            <ul className="mt-3 divide-y divide-gray-100 dark:divide-white/5">
                {rows.map(row => (
                    <PendingCard
                        key={row.id}
                        row={row}
                        method={method}
                        sellOnly={sellOnly}
                        checked={selected.has(row.id)}
                        busy={busy}
                        canFinalize={canFinalize}
                        canDeleteRecord={canDeleteRecord}
                        onToggle={onToggle}
                        onConfirm={onConfirm}
                        onReturn={onReturn}
                        onDelete={onDelete}
                        onOpen={onOpen}
                    />
                ))}
            </ul>
        </section>
    );
};

// ── Строка вещи ────────────────────────────────────────────────────────────────

interface CardProps {
    row: PendingRow;
    method: DisposalIntent;
    sellOnly: boolean;
    checked: boolean;
    busy: boolean;
    canFinalize: boolean;
    canDeleteRecord: boolean;
    onToggle: (id: string) => void;
    onConfirm: (ids: string[], method: DisposalIntent) => void;
    onReturn: (ids: string[]) => void;
    onDelete: (item: ClothingItem) => void;
    onOpen: (id: string) => void;
}

const PendingCard: React.FC<CardProps> = ({
    row, method, sellOnly, checked, busy, canFinalize, canDeleteRecord,
    onToggle, onConfirm, onReturn, onDelete, onOpen,
}) => {
    const heldDays = row.pending?.heldDays;
    return (
        <li className="py-3 flex flex-wrap gap-3" data-testid="pending-row">
            {canFinalize && !sellOnly && (
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(row.id)}
                    aria-label={`Выбрать «${row.name}»`}
                    className="mt-1 w-5 h-5 shrink-0 accent-brand-600"
                />
            )}
            <button
                type="button"
                onClick={() => onOpen(row.id)}
                tabIndex={-1}
                aria-hidden="true"
                className="shrink-0"
            >
                {row.firstPhotoUrl ? (
                    <AuthImage imageKey={row.firstPhotoUrl} alt={row.name} variant="thumbnail"
                               className="w-16 h-16 rounded-lg object-cover"/>
                ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-ink-700 flex items-center
                                    justify-center text-gray-300 dark:text-gray-600">
                        <ShirtIcon className="w-7 h-7"/>
                    </div>
                )}
            </button>

            <div className="min-w-0 flex-1 space-y-1">
                <Link to={`/clothing/${row.id}`}
                      className="font-medium text-gray-900 dark:text-gray-100 hover:text-brand-600
                                 dark:hover:text-brand-400 hover:underline break-words">
                    {row.name}
                </Link>
                <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                    {[row.brandName, row.sizeName && `размер ${row.sizeName}`, row.categoryName]
                        .filter(Boolean).join(' · ') || 'Без описания'}
                </p>
                <p data-testid="pending-fate"
                   className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
                              bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                    {fateLabel(row.disposalIntent)}
                </p>
                {heldDays != null && (
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="pending-held">
                        Лежит {pluralDays(heldDays)}
                        {row.pending?.decisionSource === 'MANUAL'
                            ? ' · решение принято вручную'
                            : ' · решение принято в разборе'}
                    </p>
                )}
            </div>

            <div className="w-full sm:w-auto sm:min-w-[14rem] space-y-2">
                {canFinalize && !sellOnly && (
                    <PendingCardActions row={row} method={method} busy={busy} onConfirm={onConfirm}/>
                )}
                <PendingMoreMenu
                    name={row.name}
                    busy={busy}
                    canDeleteRecord={canDeleteRecord}
                    onReturn={() => onReturn([row.id])}
                    onDelete={() => onDelete(row)}
                />
            </div>
        </li>
    );
};

/**
 * Основной CTA и смена способа (D11). Запланированный способ — единственная primary-кнопка
 * карточки: чаще всего человек подтверждает именно его. Остальные три способа живут за
 * «Изменить способ…» вместе с определениями — без них разница между «Отдать» и «Пожертвовать»
 * существует только в голове автора.
 */
const PendingCardActions: React.FC<{
    row: PendingRow;
    method: DisposalIntent;
    busy: boolean;
    onConfirm: (ids: string[], method: DisposalIntent) => void;
}> = ({row, method, busy, onConfirm}) => {
    const [open, setOpen] = useState(false);

    return (
        <div className="space-y-2">
            <button
                type="button"
                disabled={busy}
                onClick={() => onConfirm([row.id], method)}
                data-testid="pending-confirm"
                className="w-full min-h-[44px] px-4 rounded-xl bg-brand-600 text-white
                           text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
            >
                {DISPOSAL_METHOD_LABELS[method]}
            </button>
            <button
                type="button"
                disabled={busy}
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                data-testid="pending-change-method"
                className="w-full min-h-[44px] px-4 rounded-xl border border-gray-200 dark:border-ink-700
                           text-sm text-gray-700 dark:text-gray-200 hover:border-brand-500 disabled:opacity-50"
            >
                Изменить способ…
            </button>
            {open && (
                <ul className="rounded-xl border border-gray-200 dark:border-ink-700 divide-y
                               divide-gray-100 dark:divide-white/5"
                    data-testid="pending-method-options">
                    {INTENT_OPTIONS.map(o => (
                        <li key={o.value}>
                            <button type="button" disabled={busy}
                                    onClick={() => onConfirm([row.id], o.value)}
                                    data-testid={`pending-confirm-${o.value}`}
                                    className="w-full min-h-[44px] px-3 py-2 text-left disabled:opacity-50
                                               hover:bg-gray-50 dark:hover:bg-white/5">
                                <span className="block text-sm text-gray-900 dark:text-gray-100">
                                    {DISPOSAL_METHOD_LABELS[o.value]}
                                </span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400">
                                    {DISPOSAL_METHOD_HINTS[o.value]}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/**
 * Меню дополнительных действий (D11). Возврат в гардероб и удаление ошибочной записи — редкие
 * действия, а одно из них ещё и разрушительное: держать их наравне с подтверждением способа
 * значит приглашать к ошибке. Escape и клик вне закрывают меню, фокус возвращается на кнопку.
 */
const PendingMoreMenu: React.FC<{
    name: string;
    busy: boolean;
    canDeleteRecord: boolean;
    onReturn: () => void;
    onDelete: () => void;
}> = ({name, busy, canDeleteRecord, onReturn, onDelete}) => {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const close = useCallback(() => {
        setOpen(false);
        triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, close]);

    const item = 'w-full px-4 py-2 min-h-[44px] text-left text-sm flex items-center gap-2 '
        + 'hover:bg-gray-100 dark:hover:bg-ink-700 disabled:opacity-50';

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                disabled={busy}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Другие действия: ${name}`}
                onClick={() => setOpen(v => !v)}
                data-testid="pending-more"
                className="w-full min-h-[44px] px-4 rounded-xl text-sm text-gray-600 dark:text-gray-300
                           hover:bg-gray-100 dark:hover:bg-ink-700 disabled:opacity-50
                           inline-flex items-center justify-center gap-2"
            >
                <EllipsisVerticalIcon className="w-4 h-4"/>
                Ещё
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" aria-hidden="true" onClick={close}/>
                    <div role="menu" aria-label={`Другие действия: ${name}`} data-testid="pending-more-menu"
                         className="fw-popover absolute right-0 z-20 mt-1 min-w-[15rem] py-1 rounded-xl
                                    bg-white dark:bg-ink-800 border border-gray-200 dark:border-ink-700 shadow-lg">
                        <button role="menuitem" type="button" disabled={busy}
                                onClick={() => { close(); onReturn(); }}
                                data-testid="pending-return"
                                className={`${item} text-gray-700 dark:text-gray-200`}>
                            Вернуть в гардероб
                        </button>
                        {canDeleteRecord && (
                            <button role="menuitem" type="button" disabled={busy}
                                    onClick={() => { close(); onDelete(); }}
                                    data-testid="pending-delete-record"
                                    className={`${item} text-rose-600 dark:text-rose-400`}>
                                <AlertTriangleIcon className="w-3.5 h-3.5"/>
                                Удалить ошибочную запись
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default TrashBinPage;
